import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { CurrencyInput } from 'ui-components/components/currency-input';
import { testId } from 'ui-components/services/testid-service';
import { FlexRow } from 'ui-components/components/flex';

import type { Currency } from '../../../../prop-types/currency-types';
import commonStyle from '../common-style.module.less';

export type DeclaredValueInputProps = {
  declaredAmount: Currency;
  onChange: (value: number, code: string) => void;
  disabled?: boolean;
  'aria-label'?: string;
};

export const DeclaredValueInput = (props: DeclaredValueInputProps) => {
  const { declaredAmount: originalDeclaredAmount, disabled = false, onChange: externalOnChange, 'aria-label': ariaLabel } = props;
  const verifiedDeclaredValue = typeof originalDeclaredAmount.value === 'undefined' ? '' : originalDeclaredAmount.value;
  const [declaredValue, setDeclaredValue] = useState<number | string>(verifiedDeclaredValue);
  const verifiedDeclaredCode = originalDeclaredAmount ? originalDeclaredAmount.code : '';
  const isFocusedRef = useRef(false);

  useEffect(() => {
    if (isFocusedRef.current) {
      return;
    }
    setDeclaredValue(verifiedDeclaredValue);
  }, [verifiedDeclaredValue, setDeclaredValue]);

  const onChange = useCallback(
    (updatedDeclaredValue: number | string | null | undefined) => {
      setDeclaredValue(updatedDeclaredValue ?? '');
    },
    [setDeclaredValue],
  );

  const onBlur = useCallback(() => {
    isFocusedRef.current = false;
    externalOnChange(Number(declaredValue), verifiedDeclaredCode ?? '');
  }, [externalOnChange, declaredValue, verifiedDeclaredCode]);

  const onFocus = useCallback(() => {
    isFocusedRef.current = true;
  }, []);

  return (
    <FlexRow>
      <CurrencyInput
        {...testId('customs-widget-declared-value-input')}
        aria-label={ariaLabel}
        className={classNames(commonStyle['currency-input'])}
        currencyCode={verifiedDeclaredCode}
        disabled={disabled}
        onBlur={onBlur}
        onChange={onChange}
        onFocus={onFocus}
        value={declaredValue}
      />
      <div className={commonStyle['vert-center']}>
        <span className={commonStyle['currency-code']}>{verifiedDeclaredCode}</span>
      </div>
    </FlexRow>
  );
};
